/* eslint-disable react/prop-types */
// Argon Dashboard 2 MUI components
import { useState, useEffect } from "react";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Button, Chip } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "utils/api";
import { getAllCyclesForTutor } from "services/financeService";

const columns = [
  { name: "S.No", align: "center" },
  { name: "Type", align: "center" },
  { name: "Tutor Name", align: "left" },
  { name: "Parent Name", align: "left" },
  {name: "Student Name", align: "left"},
  { name: "Area", align: "left" },
  { name: "City", align: "center" },
  { name: "Class Name", align: "left" },
  { name: "Demo Date", align: "center" },
  { name: "Start Time", align: "center" },
  { name: "End Time", align: "center" },
  { name: "Status", align: "center" },
  // { name: "Badge", align: "center" },
  { name: "Days", align: "center" },
  { name: "Duration", align: "center" },
  { name: "Hours", align: "center" },
  { name: "Mode", align: "center" },
  { name: "Number of Session", align: "center" },
  { name: "Price Parent", align: "center" },
  { name: "Price Tutor", align: "center" },
  { name: "Session Per Month", align: "center" },
  // { name: "Action", align: "center" },
];

const DemoAllocatetTableData = (id) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Function to format time slot
  const formatTimeSlot = (slot) => {
    if (!slot || (!slot.startTime && !slot.endTime)) return "N/A";
    return `${slot.startTime || "--"} - ${slot.endTime || "--"}`;
  };

  // Function to get status color
  const getStatusColor = (status, type) => {
    if (type === "DEMO") {
      switch (status) {
        case "SCHEDULED":
          return "info";
        case "DONE":
        case "APPROVED":
          return "success";
        case "REJECTED":
        case "CANCELLED":
          return "error";
        case "REQUESTED":
          return "warning";
        default:
          return "secondary";
      }
    } else {
      // For NORMAL classes
      switch (status) {
        case "ACTIVE":
          return "success";
        case "INACTIVE":
        case "CANCELLED":
          return "error";
        case "COMPLETED":
          return "info";
        default:
          return "secondary";
      }
    }
  };

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    const fetchDemoAndClassData = async () => {
      try {
        setLoading(true);

        let demos = [];
        let classes = [];
        let payrollCycles = [];

        // Fetch demos with tutorId filter - with error handling
        try {
          const demosResponse = await axios.get(`/demo?tutorId=${id}&type=DEMO`);
          console.log("✅ Demos Response:", demosResponse.data);
          demos = demosResponse.data?.demos || demosResponse.data?.data || [];
        } catch (demoError) {
          console.warn("⚠️ Error fetching demos:", demoError.message);
        }

        // Fetch class sessions for tutor - try the dedicated class endpoint first
        try {
          const classesResponse = await axios.get(`/classes/get/active/classesFor/tutor/${id}`, {
            params: {
              status: "ACTIVE",
            },
          });
          console.log("✅ Classes Response:", classesResponse.data);
          classes = classesResponse.data?.classes || classesResponse.data?.data || [];
        } catch (classError) {
          console.warn("⚠️ Error fetching classes from dedicated endpoint:", classError.message);

          // Fall back to the shared demo endpoint if needed
          try {
            const altResponse = await axios.get(`/demo?tutorId=${id}&type=NORMAL`);
            console.log("✅ Alternative Classes Response (via demo endpoint):", altResponse.data);
            classes = altResponse.data?.demos || altResponse.data?.data || [];
          } catch (altError) {
            console.warn("⚠️ Alternative class endpoint also failed:", altError.message);
          }
        }

        try {
          const payrollResponse = await getAllCyclesForTutor(id);
          payrollCycles = Array.isArray(payrollResponse?.data) ? payrollResponse.data : [];
        } catch (payrollError) {
          console.warn("Error fetching tutor payroll cycles:", payrollError.message);
        }

        console.log("📊 Total Demos found:", demos.length);
        console.log("📊 Total Classes found:", classes.length);

        // Combine both demos and classes and de-duplicate by id/type
        const allAllocations = [...demos, ...classes];
        const seenKeys = new Set();
        const uniqueAllocations = allAllocations.filter((item) => {
          const itemId = item?._id || item?.id || "";
          const itemType = item?.type || "";
          const key = `${itemId}-${itemType}`;

          if (!itemId) {
            return true;
          }

          if (seenKeys.has(key)) {
            return false;
          }

          seenKeys.add(key);
          return true;
        });

        console.log("📊 Total Allocations:", uniqueAllocations.length);

        if (!uniqueAllocations.length) {
          console.log("⚠️ No allocations found for tutor:", id);
          setRows([]);
          setLoading(false);
          return;
        }

        let rowCounter = 0;
        const formattedRows = uniqueAllocations.flatMap((item, index) => {
          console.log(`📝 Processing item ${index + 1}:`, {
            type: item.type,
            tutorId: item.tutorId,
            parentId: item.parentId,
            students: item.students,
            status: item.status || item.demoStatus,
            paymentAmount: item.paymentAmount,
            duration: item.duration,
            numberOfClassesInAWeek: item.numberOfClassesInAWeek,
          });

          const isDemoType = item.type === "DEMO";

          // Get tutor name - check if populated object or just ID
          const tutorName =
            (typeof item.tutorId === "object" ? item.tutorId?.fullName : null) ||
            (typeof item.tutor === "object" ? item.tutor?.fullName || item.tutor?.name : null) ||
            (typeof item.tutorByAdmin === "object" ? item.tutorByAdmin?.fullName : null) ||
            (typeof item.tutorByStudent === "object"
              ? item.tutorByStudent?.fullName || item.tutorByStudent?.name
              : null) ||
            "N/A";

          const getValueId = (value) => {
            if (!value) return "";
            if (typeof value === "object") return String(value._id || value.id || "");
            return String(value);
          };
          const currentTutorId =
            getValueId(item.tutorId) || getValueId(item.tutor) || getValueId(item.tutorByAdmin);
          const oldTutor = item.oldTutorDetails || null;
          const oldTutorName = oldTutor?.fullName || oldTutor?.name || null;
          const shouldShowCurrentTutorRow = !currentTutorId || currentTutorId === String(id);

          // Get parent name
          const parentName =
            (typeof item.parentId === "object"
              ? item.parentId?.fullName || item.parentId?.name
              : null) || "N/A";
          const parentProfileId = getValueId(item.parentId);

          // Get students list
          const students = Array.isArray(item.students) ? item.students : [];
          const firstStudent =
            typeof students[0] === "object"
              ? students[0]
              : typeof item.tutorByStudent === "object"
              ? item.tutorByStudent
              : null;

          // Get class name from students
          const className =
            firstStudent?.class?.name ||
            firstStudent?.className ||
            (typeof item.class === "object" ? item.class?.name : null) ||
            "N/A";

          // Get location and extract area/city from address text when available
          const location = item.address || firstStudent?.address || "N/A";
          const areaMatch =
            typeof location === "string" ? location.match(/Area\s*:\s*([^,]+)/i) : null;
          const cityMatch =
            typeof location === "string" ? location.match(/City\s*:\s*([^,]+)/i) : null;

          const area = areaMatch?.[1]?.trim() || "N/A";
          const city = cityMatch?.[1]?.trim() || firstStudent?.city || item.city || "N/A";

          // Get subjects
          const subjects = Array.isArray(item.subject)
            ? item.subject
            : Array.isArray(item.subjects)
            ? item.subjects
            : [];
          const subjectNames =
            subjects.length > 0
              ? subjects
                  .map((s) => (typeof s === "object" ? s.name : s))
                  .filter(Boolean)
                  .join(", ")
              : "N/A";

          // Get time slots - handle both populated and non-populated
          const timeSlot =
            (typeof item.finalisedActiveSlot === "object" ? item.finalisedActiveSlot : null) ||
            (Array.isArray(item.preferedTimeSlots) && item.preferedTimeSlots.length > 0
              ? typeof item.preferedTimeSlots[0] === "object"
                ? item.preferedTimeSlots[0]
                : null
              : null);

          const startTime = timeSlot?.startTime || timeSlot?.start || "N/A";
          const endTime = timeSlot?.endTime || timeSlot?.end || "N/A";

          // Get status
          const status = isDemoType ? item.demoStatus || "N/A" : item.status || "N/A";

          // Get days (could be from schedule or preferences)
          const days =
            item.numberOfClassesInAWeek ||
            item.days ||
            item.preferredDays ||
            item.noOfClassesPerWeek ||
            "N/A";

          // Calculate hours from duration (convert minutes to hours)
          const hours = item.duration ? (item.duration / 60).toFixed(1) : "N/A";

          // Get pricing info on a full-package basis.
          const toNumber = (value) => {
            const num = Number(value);
            return Number.isFinite(num) ? num : null;
          };

          let priceParent = "N/A";
          let priceTutor = 0;

          const cycles = Array.isArray(item.cycles) ? item.cycles : [];

          const activeCycle =
            cycles.find((cycle) => String(cycle?.status || "").toUpperCase() === "ONGOING") ||
            cycles[cycles.length - 1] ||
            null;

          const classSessionId = String(item?._id || item?.id || "");
          const classPayrollCycles = payrollCycles.filter((cycle) => {
            const payrollClassSessionId =
              cycle?.financeId?.classSessionId?._id || cycle?.financeId?.classSessionId;
            return String(payrollClassSessionId || "") === classSessionId;
          });

          const activeCycleIndex = activeCycle ? cycles.indexOf(activeCycle) : -1;
          const activeCycleNumber =
            toNumber(activeCycle?.cycleNumber) ||
            toNumber(String(activeCycle?.name || "").match(/\d+/)?.[0]) ||
            (activeCycleIndex >= 0 ? activeCycleIndex + 1 : null);

          const matchingPayrollCycle =
            (activeCycleNumber !== null
              ? classPayrollCycles.find(
                  (cycle) => toNumber(cycle?.cycleNumber) === activeCycleNumber
                )
              : null) ||
            [...classPayrollCycles].sort(
              (a, b) =>
                (toNumber(b?.cycleNumber) || 0) - (toNumber(a?.cycleNumber) || 0)
            )[0] ||
            null;

          const directAllocatedClasses =
            toNumber(item.totalNoOfClasses) ||
            toNumber(item.totalClasses) ||
            toNumber(item.numberOfSessions) ||
            toNumber(item.totalSessions) ||
            0;
          const allocatedClassesAcrossCycles = cycles.reduce(
            (total, cycle) => total + (toNumber(cycle?.totalNoOfClasses) || 0),
            0
          );
          const overallAllocatedClasses =
            directAllocatedClasses || allocatedClassesAcrossCycles;

          const totalCycleClasses =
            toNumber(matchingPayrollCycle?.totalClasses) ||
            toNumber(activeCycle?.totalNoOfClasses) ||
            overallAllocatedClasses ||
            0;
          const completedClasses =
            toNumber(activeCycle?.completedClasses) || toNumber(item.completedClasses) || 0;

          const remainingClasses = Math.max(totalCycleClasses - completedClasses, 0);

          const parentPerClass =
            totalCycleClasses > 0
              ? Number(activeCycle?.monthlyFee || item.paymentAmount || 0) / totalCycleClasses
              : 0;
          const fullPackageParentAmount = cycles.reduce(
            (total, cycle) => total + (toNumber(cycle?.monthlyFee) || 0),
            0
          );

          const tutorPerClass =
            toNumber(matchingPayrollCycle?.hasFeeRevision ? matchingPayrollCycle.revisedPerClassSalary : 0) ||
            toNumber(matchingPayrollCycle?.tutorPayRate) ||
            toNumber(matchingPayrollCycle?.financeId?.tutorPayRate) ||
            toNumber(matchingPayrollCycle?.financeId?.classSessionId?.tutorSalary) ||
            toNumber(item.tutorSalary) ||
            toNumber(item.tutorPayRate) ||
            toNumber(item.currentTutorSalary) ||
            toNumber(item.financeId?.tutorPayRate) ||
            toNumber(item.priceTutor) ||
            toNumber(item.tutorPrice) ||
            (totalCycleClasses > 0 ? Number(activeCycle?.tutorPay || 0) / totalCycleClasses : 0);

          if (fullPackageParentAmount > 0) {
            priceParent = Math.round(fullPackageParentAmount);
          } else if (overallAllocatedClasses > 0 && parentPerClass > 0) {
            priceParent = Math.round(parentPerClass * overallAllocatedClasses);
          } else if (item.paymentAmount) {
            priceParent = item.paymentAmount;
          } else if (item.priceParent) {
            priceParent = item.priceParent;
          } else if (item.parentPrice) {
            priceParent = item.parentPrice;
          }

          if (matchingPayrollCycle && overallAllocatedClasses > 0 && tutorPerClass > 0) {
            // Payroll provides the current rate; the profile shows all sessions,
            // including sessions added through renewals.
            priceTutor = Math.round(tutorPerClass * overallAllocatedClasses);
          } else if (overallAllocatedClasses > 0 && tutorPerClass > 0) {
            priceTutor = Math.round(tutorPerClass * overallAllocatedClasses);
          }

          if (classPayrollCycles.some(payment => payment.hasFeeRevision)) {
            priceTutor = Math.round(cycles.reduce((sum, cycle, index) => {
              const payment = classPayrollCycles.find(p => Number(p.cycleNumber) === index + 1);
              if (payment?.hasFeeRevision) {
                return sum + Number(["PAID", "APPROVED"].includes(payment.status)
                  ? payment.calculatedAmount || 0 : payment.projectedTutorCost ?? payment.calculatedAmount ?? 0);
              }
              return sum + Number(cycle.tutorPay || Number(cycle.totalNoOfClasses || 0) * Number(payment?.tutorPayRate || item.tutorSalary || 0));
            }, 0));
          }
          if (priceParent !== "N/A") {
            const additionalByCycle = new Map(classPayrollCycles.map(payment => [payment.cycleNumber, Number(payment.feeRevisionCycle?.additionalFee || 0)]));
            priceParent = Number(priceParent) + [...additionalByCycle.values()].reduce((sum, amount) => sum + amount, 0);
          }

          // fallback for demo / no cycle
          if (priceParent === "N/A" && firstStudent?.currentSubscription) {
            priceParent =
              firstStudent.currentSubscription.amount ||
              firstStudent.currentSubscription.price ||
              "N/A";
          }

          // Fallback: derive tutor payable from parent amount and gross margin
          const grossMarginValue = toNumber(item.grossMargin);

          if (priceTutor <= 0 && priceParent !== "N/A" && grossMarginValue !== null) {
            priceTutor = Math.round(Number(priceParent) * (1 - grossMarginValue / 100));
          }

          if (!Number.isFinite(priceTutor) || priceTutor < 0) {
            priceTutor = 0;
          }

          // Sessions per month - prefer cycle-based calculation, then fallback to weekly estimate
          let sessionPerMonth = "N/A";
          const cycleTotalClasses =
            toNumber(item.totalNoOfClasses) ||
            toNumber(item.totalClasses) ||
            toNumber(item.numberOfSessions) ||
            toNumber(item.totalSessions) ||
            null;
          const cycleMonths =
            toNumber(item.noOfMonths) ||
            toNumber(item.numberOfMonths) ||
            toNumber(item.months) ||
            null;

          if (cycleTotalClasses && cycleMonths && cycleMonths > 0) {
            sessionPerMonth = Math.round(cycleTotalClasses / cycleMonths);
          } else if (item.sessionsPerMonth) {
            sessionPerMonth = item.sessionsPerMonth;
          } else if (item.sessionPerMonth) {
            sessionPerMonth = item.sessionPerMonth;
          } else if (item.numberOfClassesInAWeek) {
            sessionPerMonth = Math.round(item.numberOfClassesInAWeek * 4.33); // Average weeks per month
          } else if (item.noOfClassesPerWeek) {
            sessionPerMonth = Math.round(item.noOfClassesPerWeek * 4.33);
          }

          // Number of sessions - use various possible fields
          let numberOfSessions =
            remainingClasses > 0 ? remainingClasses : totalCycleClasses || "N/A";
          if (item.totalNoOfClasses) {
            numberOfSessions = item.totalNoOfClasses;
          } else if (item.numberOfSessions) {
            numberOfSessions = item.numberOfSessions;
          } else if (item.totalSessions) {
            numberOfSessions = item.totalSessions;
          } else if (item.completedClasses !== undefined) {
            numberOfSessions = item.completedClasses;
          } else if (item.noOfMonths && sessionPerMonth !== "N/A") {
            numberOfSessions = Math.round(item.noOfMonths * sessionPerMonth);
          }

          const buildRow = ({ displayType, displayTutorName, displayTutorId, serialNumber }) => ({
            "S.No": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {serialNumber}
              </ArgonTypography>
            ),
            Type: (
              <Chip
                label={displayType}
                size="small"
                color={displayType === "REPLACED" ? "error" : isDemoType ? "warning" : "primary"}
                variant="outlined"
              />
            ),
            "Tutor Name": (
              <ArgonTypography
                variant="caption"
                fontWeight="medium"
                color={displayTutorId ? "primary" : "text"}
                component={displayTutorId ? Link : "span"}
                to={displayTutorId ? `/tutors/profile/${displayTutorId}` : undefined}
                sx={{
                  cursor: displayTutorId ? "pointer" : "default",
                  textDecoration: displayTutorId ? "underline !important" : "none",
                  textUnderlineOffset: displayTutorId ? "2px" : undefined,
                  textDecorationThickness: displayTutorId ? "1px" : undefined,
                  "&:hover": displayTutorId
                    ? {
                        color: "#1976d2",
                        textDecoration: "underline !important",
                      }
                    : {},
                }}
              >
                {displayTutorName}
              </ArgonTypography>
            ),
            "Parent Name": (
              <ArgonTypography
                variant="caption"
                fontWeight="medium"
                color={parentProfileId ? "primary" : "text"}
                component={parentProfileId ? Link : "span"}
                to={parentProfileId ? `/parents/profile/${parentProfileId}` : undefined}
                sx={{
                  cursor: parentProfileId ? "pointer" : "default",
                  textDecoration: parentProfileId ? "underline !important" : "none",
                  textUnderlineOffset: parentProfileId ? "2px" : undefined,
                  textDecorationThickness: parentProfileId ? "1px" : undefined,
                  "&:hover": parentProfileId
                    ? {
                        color: "#1976d2",
                        textDecoration: "underline !important",
                      }
                    : {},
                }}
              >
                {parentName}
              </ArgonTypography>
            ),
            "Student Name": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {firstStudent?.fullName || firstStudent?.name || "N/A"}
              </ArgonTypography>
            ),
            Area: (
              <ArgonTypography
                variant="caption"
                fontWeight="medium"
                color="text"
                sx={{
                  maxWidth: "150px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={area !== "N/A" ? area : location}
              >
                {area}
              </ArgonTypography>
            ),
            City: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {city}
              </ArgonTypography>
            ),
            "Class Name": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {className}
              </ArgonTypography>
            ),
            "Demo Date": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {formatDate(
                  item.demoDate ||
                    item.preferredDate ||
                    item.preferedDate ||
                    item.scheduleDate ||
                    item.createdAt
                )}
              </ArgonTypography>
            ),
            "Start Time": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {startTime}
              </ArgonTypography>
            ),
            "End Time": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {endTime}
              </ArgonTypography>
            ),
            Status: (
              <ArgonBadge
                variant="gradient"
                badgeContent={status || "N/A"}
                color={getStatusColor(status, item.type)}
                size="sm"
                container
              />
            ),
            Badge: (
              <Chip
                label={item.badge || "N/A"}
                size="small"
                color="success"
                variant="outlined"
                sx={{
                  maxWidth: "120px",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            ),
            Days: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {days}
              </ArgonTypography>
            ),
            Duration: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {item.duration ? `${item.duration} min` : "N/A"}
              </ArgonTypography>
            ),
            Hours: (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {hours !== "N/A" ? `${hours} hrs` : "N/A"}
              </ArgonTypography>
            ),
            Mode: (
              <Chip
                label={item.mode || "N/A"}
                size="small"
                color={item.mode === "ONLINE" ? "info" : "default"}
                variant="outlined"
              />
            ),
            "Number of Session": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {numberOfSessions}
              </ArgonTypography>
            ),
            "Price Parent": (
              <ArgonTypography variant="caption" fontWeight="medium" color="success">
                {priceParent !== "N/A" ? `₹${priceParent}` : "N/A"}
              </ArgonTypography>
            ),
            "Price Tutor": (
              <ArgonTypography variant="caption" fontWeight="medium" color="info">
                {`₹${priceTutor}`}
              </ArgonTypography>
            ),
            "Session Per Month": (
              <ArgonTypography variant="caption" fontWeight="medium" color="text">
                {sessionPerMonth !== "N/A" ? `${sessionPerMonth} classes/month` : "N/A"}
              </ArgonTypography>
            ),
            Action: (
              <ArgonBox display="flex" alignItems="center" gap={1}>
                <Button
                  variant="text"
                  size="small"
                  component={Link}
                  to={isDemoType ? `/demo/${item._id}` : `/class/${item._id}`}
                  sx={{ minWidth: "auto", p: 0.5 }}
                >
                  <VisibilityIcon fontSize="small" color="primary" />
                </Button>
              </ArgonBox>
            ),
          });

          const allocationRows = [];
          if (shouldShowCurrentTutorRow) {
            allocationRows.push(
              buildRow({
                displayType: isDemoType ? "DEMO" : "CLASS",
                displayTutorName: tutorName,
                displayTutorId: currentTutorId,
                serialNumber: ++rowCounter,
              })
            );
          }

          if (!isDemoType && oldTutorName) {
            allocationRows.push(
              buildRow({
                displayType: "REPLACED",
                displayTutorName: oldTutorName,
                displayTutorId: getValueId(oldTutor),
                serialNumber: ++rowCounter,
              })
            );
          }

          return allocationRows;
        });

        console.log("✅ Formatted rows:", formattedRows.length);
        setRows(formattedRows);
      } catch (error) {
        console.error("❌ Error fetching demo/class allocations:", error);
        console.error("Error details:", error.response?.data || error.message);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDemoAndClassData();
  }, [id]);

  return { columns, rows, loading };
};

export default DemoAllocatetTableData;

